import { ArchiveRestore, Download, Eye, Loader2, Pencil, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { QuoteStatus } from '@/types';
import type { Quote } from '@/types';
import { quoteValueLabel, relativeDays } from './pipelineFormat';
import { QuoteStatusBadge } from './QuoteBadges';
import {
  canViewQuote,
  getQuoteDealLabel,
  getQuoteDealUrl,
  getQuoteOwnerLabel,
  getViewButtonTitle,
  getViewLabel,
  isOpenQuoteStatus,
} from './quoteMeta';
import { QuoteSignalBadges } from './QuoteSignalBadges';
import { QuoteSignatureStatus } from './QuoteSignatureStatus';
import { QuoteRowActions, type QuoteActionHandlers, type QuoteBusyState } from './QuoteRowActions';
import { describeExpiry, needsUboCountersign } from './quoteSignals';
import { formatSignedDate, getQuoteSignedAt } from './signatureStatus';

/**
 * De kaartweergave toont dezelfde velden als de lijst, in dezelfde volgorde
 * van belangrijkheid: status, waar het over gaat, wat het waard is, wanneer het
 * verloopt, wat je ermee kunt. De voet staat met `mt-auto` onderaan zodat de
 * knoppen van kaarten naast elkaar op één lijn liggen.
 */
export function QuoteCard({
  quote,
  portalId,
  selected,
  selectionMode,
  onToggleSelect,
  actions,
  busy,
}: {
  quote: Quote;
  portalId?: string;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect: () => void;
  actions: QuoteActionHandlers;
  busy: QuoteBusyState;
}) {
  const now = Date.now();
  const dealLabel = getQuoteDealLabel(quote);
  const dealUrl = getQuoteDealUrl(quote, portalId);
  const ownerLabel = getQuoteOwnerLabel(quote);
  const viewLabel = getViewLabel(quote);
  const expiry = describeExpiry(quote, now);
  const isExpired = quote.status === QuoteStatus.EXPIRED;
  const isOpen = isOpenQuoteStatus(quote.status);
  const needsCountersign = needsUboCountersign(quote);
  const isPdfDownloading = busy.pdfDownloadBusy === quote.id;
  const signedAt = getQuoteSignedAt(quote);

  return (
    <Card
      className={cn(
        'flex h-full flex-col gap-3 p-4 transition-shadow hover:shadow-md',
        selected && 'ring-2 ring-primary bg-primary/5',
      )}
      onClick={selectionMode ? onToggleSelect : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selectionMode && (
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              aria-label={`${quote.title} selecteren`}
            />
          )}
          <QuoteStatusBadge quote={quote} />
          <QuoteSignalBadges quote={quote} compact />
        </div>
        {!selectionMode && <QuoteRowActions quote={quote} actions={actions} busy={busy} />}
      </div>

      <div className="flex flex-col gap-1">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground" title={quote.title}>
          {quote.title}
        </h3>
        <p className="truncate text-sm text-muted-foreground">
          {[quote.companyName, ownerLabel].filter(Boolean).join(' · ') || 'Geen bedrijf gekoppeld'}
        </p>
        {dealLabel &&
          (dealUrl ? (
            <a
              href={dealUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open HubSpot deal ${dealLabel}`}
              className="truncate text-xs text-[#0091ae] hover:underline"
            >
              Deal: {dealLabel}
            </a>
          ) : (
            <span className="truncate text-xs text-muted-foreground">Deal: {dealLabel}</span>
          ))}
      </div>

      <QuoteSignatureStatus quote={quote} compact />

      <div className="flex items-baseline justify-between gap-2 border-t pt-3">
        <span
          className="text-xl font-bold tabular-nums"
          title={quote.valueCents ? 'Totaal van de productregels' : 'Nog geen investering ingevuld'}
        >
          {quoteValueLabel(quote)}
        </span>
        {(viewLabel || !quote.valueCents) && (
          <span className="text-xs text-muted-foreground">{viewLabel || 'geen bedrag ingevuld'}</span>
        )}
      </div>

      {/* Na ondertekening telt alleen nog wanneer dat gebeurde, niet verloop of laatste wijziging. */}
      {signedAt ? (
        <p className="text-xs text-muted-foreground">Getekend op {formatSignedDate(signedAt)}</p>
      ) : (
        <p className={cn('text-xs', expiry.urgent ? 'font-medium text-[#b8600b]' : 'text-muted-foreground')}>
          {quote.expiresAt ? `Verloopt ${expiry.label}` : 'Geen verloopdatum'} · gewijzigd{' '}
          {relativeDays(quote.updatedAt, now)}
        </p>
      )}

      {!selectionMode && (
        <div className="mt-auto flex items-center gap-1.5 border-t pt-3" onClick={(e) => e.stopPropagation()}>
          <Button variant="secondary" size="sm" onClick={actions.onEdit} className="shrink-0 gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Bewerken
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={actions.onView}
            disabled={!canViewQuote(quote)}
            aria-label="Offerte bekijken"
            title={getViewButtonTitle(quote)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={actions.onDownloadPdf}
            disabled={Boolean(busy.pdfDownloadBusy)}
            aria-label="Offerte downloaden als PDF"
            title={isPdfDownloading ? 'PDF genereren…' : 'Downloaden als PDF'}
          >
            {isPdfDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
          {quote.archivedAt ? (
            <Button variant="ghost" size="sm" onClick={actions.onRestore} className="ml-auto shrink-0 gap-1.5">
              <ArchiveRestore className="h-3.5 w-3.5" />
              Terugzetten
            </Button>
          ) : needsCountersign ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={actions.onOpenCountersign}
              disabled={busy.countersignLinkBusy === quote.id}
              className="ml-auto shrink-0 gap-1.5 text-[#b8600b] hover:text-[#b8600b]"
            >
              {busy.countersignLinkBusy === quote.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Pencil className="h-3.5 w-3.5" />
              )}
              Link openen
            </Button>
          ) : isExpired ? (
            <Button variant="ghost" size="sm" onClick={actions.onExtend} className="ml-auto shrink-0 gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
              Verlengen
            </Button>
          ) : isOpen ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={actions.onWithdraw}
              className="ml-auto shrink-0 gap-1.5 text-destructive hover:text-destructive"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Intrekken
            </Button>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export default QuoteCard;
